import { ApiError } from '@/api/backend/types';
import { getLocalizedValue } from '@/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@opaz/ui-components';
import { AxiosError } from 'axios';
import { ErrorResponse } from 'oidc-client-ts';
import { useTranslation } from 'react-i18next';
import { forwardRef, HTMLAttributes } from 'react';

export type FormErrorAlertProps = {
  error: AxiosError | ErrorResponse | 'default' | null | undefined;
} & HTMLAttributes<HTMLDivElement>;

export const FormErrorAlert = forwardRef<HTMLDivElement, FormErrorAlertProps>(({ error, ...props }, ref) => {
  const { t, i18n } = useTranslation();

  if (!error) {
    return null;
  }

  if (error instanceof AxiosError && error.response && error.response.data) {
    const apiError = error.response.data as ApiError;

    if (!apiError.message) {
      return <DefaultErrorAlert />;
    }

    return (
      <Alert variant="destructive" ref={ref} {...props}>
        <AlertTitle>{t('form.error.general.title')}</AlertTitle>
        <AlertDescription>
          {getLocalizedValue(apiError.message, i18n.language)}
          {apiError.correlationId ? (
            <>
              <br />
              {t('api.errors.referenceId', { id: apiError.correlationId })}
            </>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  }
  // invalid_otp Invalid OTP code
  if (error instanceof ErrorResponse) {
    if (!error.error_description) {
      return <DefaultErrorAlert />;
    }

    // Show generic message for invalid credentials
    let errorMessage = '';

    if (error.error === 'invalid_otp') {
      errorMessage = t('form.error.invalidOtp.description');
    } else if (
      error.error === 'invalid_password' ||
      error.error === 'unknown_user' ||
      error.error === 'invalid_grant'
    ) {
      errorMessage = t('form.error.invalidCredentials.description');
    } else {
      errorMessage = t('form.error.general.description');
    }

    return (
      <Alert variant="destructive" ref={ref} {...props}>
        <AlertTitle>{t('form.error.general.title')}</AlertTitle>
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  return <DefaultErrorAlert />;
});

FormErrorAlert.displayName = 'FormErrorAlert';

const DefaultErrorAlert = () => {
  const { t } = useTranslation();
  return (
    <Alert variant="destructive">
      <AlertTitle>{t('form.error.general.title')}</AlertTitle>
      <AlertDescription>{t('form.error.general.description')}</AlertDescription>
    </Alert>
  );
};
